import LinkIcons from '@/icons/LinkIcons'
import { formatDateWithFormatter } from '@/utils/function'
import { paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import { BusinessTripsSectionProps } from './types'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

const BusinessTripsSection = ({
    linkedBusinesses,
    linkedTickets,
    unlinkedBusinesses,
    isCompleted,
    isLinkingBusiness,
    onLinkBusiness,
}: BusinessTripsSectionProps) => {
    const { t } = useTranslation()
    const isReadOnly = useCustomerReadOnly()

    return (
        <div className="p-2 border-t border-gray-100 space-y-2">
            {/* Linked businesses */}
            {linkedBusinesses.length > 0 && (
                <>
                    <div className="text-[10px] font-medium text-blue-700 uppercase">
                        {t('business-trips')} ({linkedBusinesses.length})
                    </div>
                    {linkedBusinesses.map((note) => (
                        <Link
                            key={note.customer_journey_note_id}
                            to={generatePath(paths.businessAccountDetail, {
                                id:
                                    note.business?.business_id?.toString() ||
                                    '',
                            })}
                            target="_blank"
                            className="block p-2 bg-blue-50 rounded border border-blue-100 hover:border-blue-300 hover:bg-blue-100/50 transition-colors"
                        >
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-medium text-blue-700 hover:underline">
                                    IBN-{note.business?.business_id}
                                </span>
                                <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                        note.business?.business_status ===
                                        'approved'
                                            ? 'bg-green-100 text-green-600'
                                            : note.business?.business_status ===
                                                'rejected'
                                              ? 'bg-red-100 text-red-600'
                                              : 'bg-blue-100 text-blue-600'
                                    }`}
                                >
                                    {t(
                                        `business.${note.business?.business_status}`
                                    )}
                                </span>
                            </div>
                            {note.business?.business_content && (
                                <div className="text-[10px] text-gray-600 truncate mb-1">
                                    {note.business.business_content}
                                </div>
                            )}
                            <div className="text-[10px] text-blue-600">
                                {note.business?.business_place && (
                                    <span>
                                        {note.business.business_place} •{' '}
                                    </span>
                                )}
                                {note.business?.business_startdate && (
                                    <span>
                                        {formatDateWithFormatter(
                                            note.business.business_startdate,
                                            'DD/MM/YYYY'
                                        )}
                                        {note.business?.business_enddate &&
                                            ` - ${formatDateWithFormatter(note.business.business_enddate, 'DD/MM/YYYY')}`}
                                    </span>
                                )}
                            </div>
                        </Link>
                    ))}
                </>
            )}

            {/* Linked tickets */}
            {linkedTickets.length > 0 && (
                <>
                    <div className="text-[10px] font-medium text-purple-700 uppercase">
                        {t('meeting-tickets')} ({linkedTickets.length})
                    </div>
                    {linkedTickets.map((note) => (
                        <Link
                            key={note.customer_journey_note_id}
                            to={generatePath(paths.ticketDetail, {
                                id: note.ticket?.ticket_id?.toString() || '',
                            })}
                            target="_blank"
                            className="block p-2 bg-purple-50 rounded border border-purple-100 hover:border-purple-300 hover:bg-purple-100/50 transition-colors"
                        >
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-medium text-purple-700 hover:underline">
                                    Ticket -{note.ticket?.ticket_id}
                                </span>
                                <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                        note.ticket?.ticket_status === 'closed'
                                            ? 'bg-green-100 text-green-600'
                                            : note.ticket?.ticket_status ===
                                                'opening'
                                              ? 'bg-yellow-100 text-yellow-600'
                                              : 'bg-purple-100 text-purple-600'
                                    }`}
                                >
                                    {t(`tickets.${note.ticket?.ticket_status}`)}
                                </span>
                            </div>
                            {/* Meeting purpose */}
                            {note.ticket?.ticket_meeting_purpose && (
                                <div className="text-[10px] text-gray-600 mb-1">
                                    {t(
                                        `ticket-meeting-purpose.${note.ticket.ticket_meeting_purpose}`
                                    )}
                                </div>
                            )}
                            {/* Meeting type & via */}
                            <div className="text-[10px] text-purple-600 flex items-center gap-1">
                                {note.ticket?.ticket_meeting_type && (
                                    <span className="capitalize">
                                        {note.ticket.ticket_meeting_type}
                                    </span>
                                )}
                                {note.ticket?.ticket_requested_at && (
                                    <>
                                        <span>•</span>
                                        <span>
                                            {formatDateWithFormatter(
                                                note.ticket.ticket_requested_at,
                                                'DD/MM/YYYY HH:mm'
                                            )}
                                        </span>
                                    </>
                                )}
                            </div>
                        </Link>
                    ))}
                </>
            )}

            {/* Unlinked businesses to link (ẩn ở chế độ chỉ xem) */}
            {unlinkedBusinesses.length > 0 && !isCompleted && !isReadOnly && (
                <>
                    <div className="text-[10px] font-medium text-gray-500 uppercase mt-2">
                        {t('unlinked-business-trips')} (
                        {unlinkedBusinesses.length})
                    </div>
                    {unlinkedBusinesses.slice(0, 3).map((business) => (
                        <div
                            key={business.business_id}
                            className="p-2 bg-gray-50 rounded border border-gray-200 border-dashed"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-gray-700">
                                        IBN-{business.business_id}
                                    </div>
                                    {business.business_content && (
                                        <div className="text-[10px] text-gray-600 truncate">
                                            {business.business_content}
                                        </div>
                                    )}
                                    <div className="text-[10px] text-gray-500">
                                        {business.business_place && (
                                            <span>
                                                {business.business_place} •{' '}
                                            </span>
                                        )}
                                        {business.business_startdate && (
                                            <span>
                                                {formatDateWithFormatter(
                                                    business.business_startdate,
                                                    'DD/MM/YYYY'
                                                )}
                                                {business.business_enddate &&
                                                    ` - ${formatDateWithFormatter(business.business_enddate, 'DD/MM/YYYY')}`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                                    onClick={() =>
                                        onLinkBusiness(business.business_id)
                                    }
                                    disabled={isLinkingBusiness}
                                >
                                    <LinkIcons className="w-3 h-3" />
                                    {t('link')}
                                </button>
                            </div>
                        </div>
                    ))}
                    {unlinkedBusinesses.length > 3 && (
                        <div className="text-[10px] text-center text-gray-500">
                            +{unlinkedBusinesses.length - 3} {t('more')}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default BusinessTripsSection
